import React, { useState, useContext } from "react";
import { BaseScreen, BaseNavTabs, BaseGrid, BaseToolbar, ITableColumn, ICrud, BaseModal, FilterOperator } from "react-epharma-components/lib";
import UserContext from "react-epharma-components/lib/components/context/user";
import { AppServicesType } from "@enums/AppServicesType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faFileLines } from "@fortawesome/free-solid-svg-icons";
import { Button, OverlayTrigger, Tooltip, Modal, Alert, Spinner } from "react-bootstrap";
import { deleteAssociation } from "@services/servicesAssociationsService";
import Helpers from "@helpers";
import { FlexParameterizationContent } from "./tabs/flexParameterization";
import { EpCareContent } from "./tabs/epCare";
import Search from "./Search";
import CrudForm from "./Crud";
import { ServicesAssociationsLogActionLabels } from "@enums/ServicesAssociationsLogAction";

const ServicesAssociationsScreen: React.FC = () => {
  const [activeService, setActiveService] = useState<AppServicesType>(AppServicesType.Oxy);

  const navs = [
    { key: AppServicesType.Oxy, name: "Oxy", content: <ServiceGrid service={AppServicesType.Oxy} isActive={activeService === AppServicesType.Oxy} /> },
    { key: AppServicesType.EpStore, name: "epStore", content: <ServiceGrid service={AppServicesType.EpStore} isActive={activeService === AppServicesType.EpStore} /> },
    { key: AppServicesType.EpExpress, name: "epExpress", content: <ServiceGrid service={AppServicesType.EpExpress} isActive={activeService === AppServicesType.EpExpress} /> },
    { key: AppServicesType.EpCare, name: "epCare", content: <EpCareContent isActive={activeService === AppServicesType.EpCare} /> },
    { key: AppServicesType.EpFlex, name: "epFlex", content: <FlexParameterizationContent isActive={activeService === AppServicesType.EpFlex} /> },
    { key: AppServicesType.EpESG, name: "epESG", content: <ServiceGrid service={AppServicesType.EpESG} isActive={activeService === AppServicesType.EpESG} /> },
  ];

  return (
    <BaseScreen title="Associação de Planos aos Serviços (epharma App)" description="Gerencie as associações de planos para cada serviço.">
      <BaseNavTabs defaultActiveKey={AppServicesType.Oxy} onSelect={(key) => setActiveService(key as AppServicesType)} navs={navs} />
    </BaseScreen>
  );
};

interface IServiceGridProps {
  service: AppServicesType;
  isActive: boolean;
}

const ServiceGrid: React.FC<IServiceGridProps> = ({ service, isActive }) => {
  const { checkIfHasPermission, permissionIdList } = useContext(UserContext);
  const hasLogPermission = checkIfHasPermission("#CDLOGSVCASSOC", permissionIdList);
  const hasDeletePermission = checkIfHasPermission("#CDDELSVCASSOC", permissionIdList);

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; benefitId: number; benefitName: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showLogModal, setShowLogModal] = useState(false);

  const serviceDescriptions: Record<string, string> = {
    [AppServicesType.Oxy]: "Gestão de planos habilitados para o layout Oxy.",
    [AppServicesType.EpStore]: "Gestão de planos habilitados para o serviço epStore.",
    [AppServicesType.EpExpress]: "Gestão de planos habilitados para o serviço epExpress.",
    [AppServicesType.EpESG]: "Gestão de planos habilitados para o serviço epESG.",
  };

  const logColumns: ITableColumn[] = [
    {
      name: "action",
      displayName: "Ação",
      sortable: true,
      onRender: (value: string) => ServicesAssociationsLogActionLabels[value] || value,
    },
    {
      name: "benefitId",
      displayName: "Código do Plano",
      sortable: true,
    },
    {
      name: "benefitName",
      displayName: "Nome do Plano",
      sortable: true,
    },
    {
      name: "authorName",
      displayName: "Nome do Autor",
      sortable: true,
    },
    {
      name: "createdAt",
      displayName: "Data/Hora",
      type: "datetime",
      sortable: true,
      sorter: "desc" as any,
    },
  ];

  if (!isActive) return null;

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    setErrorMsg("");
    try {
      await deleteAssociation(service, deleteTarget.id);
      setDeleteTarget(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors && Array.isArray(errors) && errors.length > 0) {
        setErrorMsg(errors.map((e: any) => (typeof e === "string" ? e : e.message || "Erro desconhecido")).join(", "));
      } else {
        setErrorMsg("Erro ao excluir associação.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: ITableColumn[] = [
    {
      name: "id",
      displayName: "ID",
      sortable: false,
      hidden: true,
    },
    {
      name: "benefitId",
      displayName: "Código do Plano",
      sortable: false,
    },
    {
      name: "aggregatorName",
      displayName: "Agregador",
      sortable: false,
    },
    {
      name: "clientName",
      displayName: "Cliente",
      sortable: false,
    },
    {
      name: "benefitName",
      displayName: "Plano",
      sortable: false,
    },
    ...(hasDeletePermission
      ? [
          {
            name: "actions",
            displayName: "Ações",
            sortable: false,
            onRender: (_value: any, record: any) => (
              <div className="d-flex justify-content-center">
                <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-tooltip-${record.id}`}>Excluir</Tooltip>}>
                  <Button variant="danger" size="sm" aria-label="Excluir" onClick={() => setDeleteTarget({ id: record.id, benefitId: record.benefitId, benefitName: record.benefitName })}>
                    <FontAwesomeIcon icon={faTrashAlt} size="sm" />
                  </Button>
                </OverlayTrigger>
              </div>
            ),
          },
        ]
      : []),
  ];

  const crud: ICrud = {
    form: <CrudForm />,
    create: { show: true, permissionId: "#CDNEWSVCASSOC" },
    read: { show: false },
    delete: { show: false },
  };

  return (
    <div className="mt-3">
      {errorMsg && (
        <Alert variant="danger" onClose={() => setErrorMsg("")} dismissible>
          {errorMsg}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted">{serviceDescriptions[service] || ""}</span>
        {hasLogPermission && (
          <OverlayTrigger placement="top" overlay={<Tooltip id={`log-tooltip-${service}`}>Logs de Auditoria</Tooltip>}>
            <Button variant="info" size="sm" onClick={() => setShowLogModal(true)}>
              <FontAwesomeIcon icon={faFileLines} className="mr-1" /> Logs
            </Button>
          </OverlayTrigger>
        )}
      </div>

      <BaseGrid
        key={refreshKey}
        name={`grid-services-associations-${service}`}
        baseEndpoint={`${Helpers.endpoints.servicesAssociations}/${service}`}
        toolbar={
          <BaseToolbar
            crud={crud}
            search={{
              show: true,
              form: <Search />,
            }}
          />
        }
        columns={columns}
      />

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <p>
              Tem certeza de que deseja desvincular o plano{" "}
              <strong>
                {deleteTarget.benefitId} - {deleteTarget.benefitName}
              </strong>{" "}
              do serviço <strong>{service}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-1" /> Excluindo...
              </>
            ) : (
              "Excluir"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BaseModal
        name={`LogModalServicesAssociations-${service}`}
        size="xl"
        show={showLogModal}
        title={`Logs de Auditoria — ${service}`}
        content={
          showLogModal ? (
            <BaseGrid
              name={`grid-services-associations-log-${service}`}
              baseEndpoint={`${Helpers.endpoints.servicesAssociationsLog}`}
              filter={[{ property: "ServiceType", operator: FilterOperator.Equals, value: service }]}
              columns={logColumns}
            />
          ) : undefined
        }
        callbackAfterClose={() => setShowLogModal(false)}
      />
    </div>
  );
};

export default ServicesAssociationsScreen;
